import { Autocomplete, Box, Button, Checkbox, Chip, FormControlLabel, FormHelperText, Grid, IconButton, TextField } from "@mui/material";
import GlobalDialogContent from "../GlobalDialogContent";
import { FieldArray, FormikProvider, useFormik } from "formik";
import { AttachFile, Delete } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import * as Yup from "yup";
import { showSnackbar } from "../../../redux/reducer/snackbarSlice";
import { useEffect, useState } from "react";
import { BankList } from "../../../utils/services/project/project";
import { UpdateUserprofileBankService } from "../../../utils/services/registration/donor-registration.service";
import { closeDialog } from "../../../redux/reducer/dialogSlice";
import { setModuleRefresh } from "../../../redux/reducer/registration/RefreshSlice";
import PageLang from "../../../config/page.languge";

export default function UpdateUserBanks() {

    const BankData = useSelector((state: RootState) => state.dialog);

    const dispatch = useDispatch();
    const [bankList, setBankList] = useState([]);
    const [deleteIds, setDeleteids] = useState<any[]>([]);
    const [isBankListFetched, setIsBankListFetched] = useState(false); 
    const lang=useSelector((state:RootState)=>state.lang.language)

    const initialValues = {
        banks: (BankData?.payload?.bankInfo || []).map((bank: any) => ({
            id: bank?.id || "",
            name: bank?.bank_name || "",
            account: bank?.account_number || "",
            ifsc: bank?.ifsc_code || "",
            username: bank?.account_holder_name || "",
            cheque: null,
            chequePath: bank?.file?.file_path || "",
            chequeName: bank?.file?.file_name || bank?.file?.file_path?.split("/").pop() || "",
            isDefault: bank.is_default === "Y" 
        })),
    };

    const validationSchema = Yup.object().shape({
        banks: Yup.array()
            .max(3, "Only 3 banks allowed")
            .of(
                Yup.object().shape({
                    name: Yup.string().required(PageLang?.forms?.Bank?.name?.reqError[lang]),
                    account: Yup.string()
                        .required(PageLang?.forms?.Bank?.accountNumber?.reqError[lang])
                        .matches(/^\d{9,18}$/,PageLang?.forms?.Bank?.accountNumber.accountNumberMaxLength[lang]),
                    ifsc: Yup.string()
                        .required(PageLang?.forms?.Bank?.ifscCode?.reqError[lang])
                        .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, PageLang?.forms?.Bank?.ifscCode?.ifscCodeInvalid[lang]),
                    username: Yup.string().required(PageLang?.forms?.Bank?.accountHolderName.reqError[lang]),
                    cheque: Yup.mixed()
                        .nullable()
                        .test("fileSize",PageLang?.forms?.fileSizeLimit[lang] , (file: any, context: any) => {
                            if (file) {
                                return file.size <= 5 * 1024 * 1024;
                            }
                            return context.parent.chequePath ? true : false;
                        })
                        .test("fileType", "Only PDF, JPG, and PNG files are allowed", (file: any, context: any) => {
                            if (file) {
                                return ["application/pdf", "image/jpeg", "image/png", "image/jpg"].includes(file.type);
                            }
                            return context.parent.chequePath ? true : false;
                        }),
                    isDefault: Yup.boolean(),
                })
            )
            .test("oneDefault", "One account must be marked as default", (banks) => {
                return banks?.filter((bank) => bank.isDefault).length === 1;
            }),
    });
    
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: initialValues,
        validationSchema: validationSchema,
        onSubmit: async (values) => {

            const formData = new FormData();
            formData.append("current_step", "bank_info");
            formData.append("request_uuid", BankData?.payload?.uuid);
            formData.append("delete_bank_id", JSON.stringify(deleteIds));
            values.banks.forEach((item: any, index: any) => {
                if (
                    item.name &&
                    item.username &&
                    item.ifsc &&
                    item.account
                ) {
                    if (item.id) {
                    formData.append(`data[${index}][bank_id]`, item.id);
                    }
                    formData.append(`data[${index}][bank_name]`, item.name.trim());
                    formData.append(`data[${index}][account_holder_name]`, item.username.trim());
                    formData.append(`data[${index}][ifsc_code]`, item.ifsc.trim());
                    formData.append(`data[${index}][account_number]`, item.account.trim());
                    formData.append(`data[${index}][is_default]`, item.isDefault ? "Y" : "N");
                    if (item.cheque) {
                        formData.append(`data[${index}][file]`, item.cheque);
                    } else if (item.chequePath) {
                        formData.append(`data[${index}][file_path]`, item.chequePath);
                    }
                }
            });
            const response = await UpdateUserprofileBankService(formData);
            const { code, message } = response;
            if (code === 200) {
                dispatch(closeDialog());
                dispatch(showSnackbar({ type: "success", message: message || "Bank Details Updated Successfully." }));
                dispatch(setModuleRefresh({ moduleName: 'updateBank', refresh: true }));
            } else {
                dispatch(showSnackbar({ type: "error", message: message || "Something Went Wrong, Please try again" }));
            }
        }
    });

    useEffect(() => {
        if (!isBankListFetched) fetchBankList();
        
        // Only set first bank as default if there are no defaults AND there are banks
        if (
          formik.values.banks.length > 0 && 
          !formik.values.banks.some((bank: any) => bank.isDefault)
        ) {
          const updatedBanks = formik.values.banks.map((bank: any, i: number) => ({
            ...bank,
            isDefault: i === 0
          }));
          formik.setFieldValue("banks", updatedBanks);
        }
    }, [isBankListFetched]); // Add formik.values.banks to dependencies

    const fetchBankList = async () => {
        const req = {};
        const { code, data } = await BankList(req);
        if (code === 200 && data) {
            setBankList(data);
            setIsBankListFetched(true); 
        } else {
            setBankList([]);
            setIsBankListFetched(true);
        }
    };

    const handleFileChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        fieldName: string
    ) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
        const maxSize = 5 * 1024 * 1024;

        if (!allowedTypes.includes(file.type)) {
            dispatch(showSnackbar({ type: 'error', message: 'Only PDF, JPG, and PNG files are allowed.' }));
            return;
        }

        if (file.size > maxSize) {
            dispatch(showSnackbar({ type: 'error', message: PageLang?.forms?.fileSizeLimit[lang] }));
            return;
        }

        formik.setFieldValue(fieldName, file);
        formik.setFieldValue(fieldName.replace("cheque", "chequePath"), "");
        formik.setFieldValue(fieldName.replace("cheque", "chequeName"), file.name);
    };

    return (
        <>
            <GlobalDialogContent
                dialogBody={
                    <FormikProvider value={formik}>
                        <form onSubmit={formik.handleSubmit} id="Bank-Update">
                            <Box mt={2}>
                                <FieldArray name="banks">
                                    {({ push, remove }) => (
                                        <>
                                            {formik.values.banks.map((bank: any, index: any) => (
                                                <Box key={index} mb={2}>
                                                    <Grid container spacing={2} alignItems="center">
                                                        <Grid item xs={12} sm={3}>
                                                            <Autocomplete
                                                                options={bankList}
                                                                getOptionLabel={(option: any) => option.bank_name || ""}
                                                                onOpen={() => {
                                                                    if (!isBankListFetched) {
                                                                        fetchBankList();
                                                                    }
                                                                }}
                                                                value={bankList.find((b: any) => b.bank_name === bank.name) || null}
                                                                onChange={(_, newValue) => {
                                                                    formik.setFieldValue(`banks[${index}].name`, newValue?.bank_name || "");
                                                                }}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label={PageLang?.forms?.Bank?.name?.label[lang]}
                                                                        fullWidth
                                                                        size="small"
                                                                        error={Boolean(
                                                                            (formik.touched.banks as any)?.[index]?.name &&
                                                                            (formik.errors.banks as any)?.[index]?.name
                                                                        )}
                                                                        helperText={
                                                                            (formik.touched.banks as any)?.[index]?.name &&
                                                                            (formik.errors.banks as any)?.[index]?.name
                                                                        }
                                                                    />
                                                                )}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={1.5}>
                                                            <TextField
                                                                label={PageLang?.forms?.Bank?.accountNumber?.label[lang]}
                                                                fullWidth
                                                                size="small"
                                                                value={bank.account}
                                                                onChange={(e) => {
                                                                    const input = e.target.value.replace(/\D/g, "");
                                                                    formik.setFieldValue(`banks[${index}].account`, input);
                                                                }}
                                                                inputProps={{
                                                                    maxLength: 15,
                                                                }}
                                                                error={Boolean(
                                                                    (formik.touched.banks as any)?.[index]?.account &&
                                                                    (formik.errors.banks as any)?.[index]?.account
                                                                )}
                                                                helperText={
                                                                    (formik.touched.banks as any)?.[index]?.account &&
                                                                    (formik.errors.banks as any)?.[index]?.account
                                                                }
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={1.5}>
                                                            <TextField
                                                                label={PageLang?.forms.Bank.ifscCode.label[lang]}
                                                                fullWidth
                                                                size="small"
                                                                value={bank.ifsc}
                                                                onChange={(e) =>
                                                                    formik.setFieldValue(`banks[${index}].ifsc`, e.target.value.toUpperCase())
                                                                }
                                                                inputProps={{
                                                                    maxLength: 11,
                                                                }}
                                                                error={Boolean(
                                                                    (formik.touched.banks as any)?.[index]?.ifsc &&
                                                                    (formik.errors.banks as any)?.[index]?.ifsc
                                                                )}
                                                                helperText={
                                                                    (formik.touched.banks as any)?.[index]?.ifsc &&
                                                                    (formik.errors.banks as any)?.[index]?.ifsc
                                                                }


                                                            />

                                                        </Grid>
                                                        <Grid item xs={12} sm={2}>
                                                            <TextField
                                                                label={PageLang?.forms.Bank.accountHolderName.label[lang]}
                                                                fullWidth
                                                                size="small"
                                                                value={bank.username}
                                                                onChange={(e) =>
                                                                    formik.setFieldValue(`banks[${index}].username`, e.target.value)
                                                                }
                                                                error={Boolean(
                                                                    (formik.touched.banks as any)?.[index]?.username &&
                                                                    (formik.errors.banks as any)?.[index]?.username
                                                                )}
                                                                helperText={
                                                                    (formik.touched.banks as any)?.[index]?.username &&
                                                                    (formik.errors.banks as any)?.[index]?.username
                                                                }
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={2}>
                                                            {!bank.cheque && !bank.chequePath ? (
                                                                <Button
                                                                    variant="outlined"
                                                                    component="label"
                                                                    startIcon={<AttachFile />}
                                                                >
                                                                    {PageLang?.forms?.Bank?.attachment.label[lang]}
                                                                    <input
                                                                        type="file"
                                                                        hidden
                                                                        accept=".pdf,.jpg,.png"
                                                                        onChange={(e) => handleFileChange(e, `banks[${index}].cheque`)}
                                                                    />
                                                                </Button>
                                                            ) : (
                                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                    <Chip
                                                                        label={bank.cheque?.name || bank.chequeName || "Attached File"}
                                                                        sx={{ maxWidth: 150 }}
                                                                        color="primary"
                                                                        variant="outlined"
                                                                        onDelete={() => {
                                                                            formik.setFieldValue(`banks[${index}].cheque`, null);
                                                                            formik.setFieldValue(`banks[${index}].chequePath`, "");
                                                                            formik.setFieldValue(`banks[${index}].chequeName`, "");
                                                                        }}
                                                                    />
                                                                </Box>
                                                            )}
                                                            {(formik.touched.banks as any)?.[index]?.cheque &&
                                                                (formik.errors.banks as any)?.[index]?.cheque && (
                                                                    <FormHelperText error>
                                                                        {(formik.errors.banks as any)?.[index]?.cheque}
                                                                    </FormHelperText>
                                                                )}
                                                        </Grid>
                                                        <Grid item xs={12} sm={1}>
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        checked={bank.isDefault}
                                                                        onChange={(e) => {
                                                                            const updatedBanks = formik.values.banks.map((b: any, i: any) => ({
                                                                                ...b,
                                                                                isDefault: i === index && e.target.checked
                                                                            }));
                                                                            formik.setFieldValue("banks", updatedBanks);
                                                                        }}
                                                                    />
                                                                }
                                                                label="Default"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={1}>
                                                            {index > 0 && (
                                                                <IconButton 
                                                                    color="primary" 
                                                                    onClick={() => {
                                                                        if (bank.id) {
                                                                            setDeleteids(prev => [...prev, bank.id]);
                                                                        }
                                                                        remove(index);
                                                                        setTimeout(() => {
                                                                            const remainingBanks = formik.values.banks.filter((_: any, i: number) => i !== index);
                                                                            if (remainingBanks.length > 0 && !remainingBanks.some((b: any) => b.isDefault)) {
                                                                                const updatedBanks = remainingBanks.map((b: any, i: number) => ({
                                                                                    ...b,
                                                                                    isDefault: i === remainingBanks.length - 1,
                                                                                }));
                                                                                formik.setFieldValue("banks", updatedBanks);
                                                                            }
                                                                        }, 0);
                                                                    }}
                                                                >        
                                                                    <Delete />
                                                                </IconButton>
                                                            )}
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            ))}
                                            {formik.values.banks.length < 3 && (
                                                <Button
                                                    variant="outlined"
                                                    onClick={() =>
                                                        push({
                                                            name: "",
                                                            account: "",
                                                            ifsc: "",
                                                            username: "",
                                                            cheque: null,
                                                            chequePath: "",
                                                            chequeName: "",
                                                            isDefault: formik.values.banks.length === 0,
                                                        })
                                                    }
                                                >
                                                   {PageLang?.forms?.Bank.addBank[lang]} 
                                                </Button>
                                            )}
                                        </>
                                    )}
                                </FieldArray>
                            </Box>
                        </form>
                    </FormikProvider>
                }
                dialogFooter={
                    <Button
                        type="submit"
                        variant="contained"
                        form="Bank-Update"
                    >
                        {PageLang?.update[lang]}
                    </Button>
                }
            />
        </>
    )
}
